import { useEffect, useRef } from 'react';
import { Newspaper, Sparkles, Tag } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { MARKETING_CATEGORIES, type MarketingCategory } from '@evi/shared';
import type { MarketingPreferences } from '@/lib/api';
import { marketingControlState } from '@/lib/emailPreferencesFlow';
import styles from './SettingsPage.module.css';

/**
 * The marketing email controls (23 October 2026), shared by the signed-in
 * Settings page and the public /email-preferences page an email links to:
 * the three categories, each on by default, the 2-a-month note, and
 * Unsubscribe from all. Turning a category back on after an unsubscribe
 * re-subscribes to that category only.
 *
 * Each switch is a real checkbox (role="switch") inside its row's label
 * (23 October 2026, review), so Tab reaches it, Space toggles it, a click on
 * the row's text toggles it, and a disabled switch says so to assistive
 * technology. The styled track beside it is only its picture.
 *
 * Focus stays put across a save (23 October 2026, second review). The native
 * `disabled` attribute moves focus to the page body the moment it lands on
 * the focused switch, so a save used it for every switch and a keyboard or
 * screen reader user lost their place on every toggle. Now `disabled` is set
 * only while there is nothing to show yet; during a save the switches are
 * aria-disabled and ignore presses. Unsubscribe from all is replaced by the
 * "You are unsubscribed" heading once it succeeds, so focus moves to that
 * heading.
 */

const ICONS: Record<MarketingCategory, React.ComponentType<{ size: number }>> = {
  offers: Tag,
  product_updates: Sparkles,
  company_news: Newspaper,
};

export function MarketingEmailControls({
  value, disabled = false, onToggle, onUnsubscribeAll,
}: {
  value: MarketingPreferences | undefined;
  /** A save is in flight: presses are ignored, focus stays. */
  disabled?: boolean;
  onToggle: (category: MarketingCategory, on: boolean) => void;
  onUnsubscribeAll: () => void;
}) {
  const { t } = useTranslation('emailPreferences');
  const unsubscribed = value?.unsubscribed === true;
  const state = marketingControlState({ loading: !value, saving: disabled });
  const unsubscribedHeading = useRef<HTMLParagraphElement>(null);
  const focusUnsubscribedHeading = useRef(false);
  useEffect(() => {
    if (unsubscribed && focusUnsubscribedHeading.current) {
      focusUnsubscribedHeading.current = false;
      unsubscribedHeading.current?.focus();
    }
  }, [unsubscribed]);
  // An unsubscribe that did not go through leaves focus on its button.
  useEffect(() => {
    if (!disabled && !unsubscribed) focusUnsubscribedHeading.current = false;
  }, [disabled, unsubscribed]);
  return (
    <>
      {MARKETING_CATEGORIES.map((c) => {
        const Icon = ICONS[c];
        const on = !unsubscribed && value?.categories[c] === true;
        return (
          <label
            key={c}
            className={styles.notifRow}
            style={state.inert ? { opacity: 0.6, cursor: 'default' } : undefined}
          >
            <div className={styles.notifIcon}><Icon size={16} /></div>
            <div className={styles.notifInfo}>
              <p className={styles.notifLabel}>{t(`categories.${c}.label`)}</p>
              <p className={styles.notifDesc}>{t(`categories.${c}.desc`)}</p>
            </div>
            <input
              type="checkbox"
              role="switch"
              className={styles.switchInput}
              checked={on}
              disabled={state.disabled}
              aria-disabled={state.ariaDisabled}
              aria-label={t(`categories.${c}.label`)}
              onChange={(e) => { if (!state.inert) onToggle(c, e.target.checked); }}
            />
            <span aria-hidden="true" className={`${styles.switch} ${on ? styles.switchOn : ''}`}>
              <span className={styles.switchKnob} />
            </span>
          </label>
        );
      })}
      {unsubscribed ? (
        <div>
          <p className={styles.notifLabel} ref={unsubscribedHeading} tabIndex={-1}>{t('unsubscribedTitle')}</p>
          <p className={styles.notifDesc}>{t('unsubscribedDesc')}</p>
        </div>
      ) : (
        <button
          type="button"
          className={styles.unsubscribeAllBtn}
          disabled={state.disabled}
          aria-disabled={state.ariaDisabled}
          onClick={() => {
            if (state.inert) return;
            focusUnsubscribedHeading.current = true;
            onUnsubscribeAll();
          }}
        >
          {t('unsubscribeAll')}
        </button>
      )}
      <p className={styles.notifNote}>{t('cap')}</p>
    </>
  );
}
